import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import {
  Calendar24Regular,
  Settings24Regular,
  ChevronRight24Regular,
  ChevronLeft24Regular,
  ClipboardTaskListLtr24Regular,
  People24Regular,
  VehicleTruckProfile24Regular,
  CodeBlock24Regular,
  Map24Regular,
  MoreHorizontal24Regular,
  TaskListSquareLtr24Regular,
  Wrench24Regular,
  DocumentText24Regular,
  Box24Regular,
  Stack24Regular,
  Chat24Regular,
  Calculator24Regular,
  History24Regular,
  Cloud24Regular,
  PersonAccounts24Regular,
  PeopleSettings24Regular,
  TextBulletList24Regular,
} from '@fluentui/react-icons';
import '../styles/leftrail.css';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useProxyRole, ProxyRole } from '../contexts/ProxyRoleContext';
import { canSeeNavPath } from '../config/roleAccess';

interface LeftRailProps {
  onNavigate?: () => void;
}

export default function LeftRail({ onNavigate }: LeftRailProps) {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const { user } = useAdminAuth();
  const { proxyRole } = useProxyRole();

  // Effective role: proxy override wins, otherwise the user's stored role.
  // Each item's visibility is decided by the shared canSeeNavPath() so the rail
  // can never drift from the route guard (config/roleAccess). Owner/admin see
  // everything; dispatcher + office see a curated subset; driver sees nothing.
  const effectiveRole: ProxyRole = proxyRole ?? user?.role ?? 'office';
  const canSee = (path: string) => canSeeNavPath(effectiveRole, path);

  const mainNavItems = [
    //{ icon: Map24Regular, path: '/operations-map', label: 'Operations Map' },
    //{ icon: Calendar24Regular, path: '/booking', label: 'Booking' },
    { icon: People24Regular, path: '/customers', label: 'Customers' },
    { icon: ClipboardTaskListLtr24Regular, path: '/orders', label: 'Orders' },
    // Projects retired — order phases supersede it. Route still exists; hidden from rail.
    //{ icon: Stack24Regular, path: '/projects', label: 'Projects' },
    { icon: VehicleTruckProfile24Regular, path: '/loadboard', label: 'Loadboard' },
    { icon: Box24Regular, path: '/product_groups', label: 'Inventory' },
    // { icon: TextBulletList24Regular, path: '/logs', label: 'Logs' },
  ].filter((item) => canSee(item.path));

  // Overflow nav
  const overflowNavItems = [
    { icon: Chat24Regular, path: '/messaging', label: 'Messaging' },
    { icon: TaskListSquareLtr24Regular, path: '/tasks', label: 'Tasks' },
    { icon: Wrench24Regular, path: '/maintenance', label: 'Maintenance' },
    { icon: DocumentText24Regular, path: '/documents', label: 'Documents' },
    { icon: Calculator24Regular, path: '/financial', label: 'Financial' },
    { icon: TextBulletList24Regular, path: '/leads', label: 'Leads' },
    { icon: History24Regular, path: '/activity', label: 'Activity' },
    { icon: Cloud24Regular, path: '/website', label: 'Website' },
    { icon: PersonAccounts24Regular, path: '/portal', label: 'Customer Portal', openInNewTab: true },
  ].filter((item) => canSee(item.path));

  const bottomNavItems = [
    { icon: PeopleSettings24Regular, path: '/users', label: 'Users' },
    { icon: CodeBlock24Regular, path: '/dev', label: 'Dev' },
    { icon: Settings24Regular, path: '/settings', label: 'Settings' },
  ].filter((item) => canSee(item.path));

  const hasActiveOverflow = overflowNavItems.some(
    item => location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  );

  // A modified click (Ctrl/Cmd/Shift) on a real anchor is the browser's
  // open-in-new-tab/window gesture — let it through untouched. Middle-click
  // fires `auxclick`, not `click`, so it never reaches onClick at all.
  const isModifiedClick = (e: React.MouseEvent) =>
    e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0;

  const renderButton = (item: { icon: any; path: string; label: string }) => {
    const IconComponent = item.icon;
    const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`left-rail-button ${isActive ? 'active' : ''}`}
        onClick={(e) => { if (!isModifiedClick(e)) onNavigate?.(); }}
        title={item.label}
        aria-label={item.label}
      >
        <IconComponent />
        {isExpanded && <span className="left-rail-label">{item.label}</span>}
      </Link>
    );
  };

  return (
    <nav className={`left-rail ${isExpanded ? 'left-rail-expanded' : ''}`}>
      <div className="left-rail-items">
        {mainNavItems.map(renderButton)}

        {overflowNavItems.length > 0 && (
          <Menu positioning={{ position: 'after', align: 'start' }}>
            <MenuTrigger disableButtonEnhancement>
              <button
                className={`left-rail-button ${hasActiveOverflow ? 'active' : ''}`}
                title="More"
                aria-label="More navigation items"
              >
                <MoreHorizontal24Regular />
                {isExpanded && <span className="left-rail-label">More</span>}
              </button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {overflowNavItems.map(item => {
                  const IconComponent = item.icon;
                  // Always-new-tab items (e.g. Customer Portal) render as a plain
                  // anchor with target=_blank; the rest render as a router Link so
                  // a plain click does SPA nav while Ctrl/Cmd/middle-click opens a
                  // new tab natively.
                  const linkProps: any = item.openInNewTab
                    ? { as: 'a', href: item.path, target: '_blank', rel: 'noopener noreferrer' }
                    : { as: Link, to: item.path };
                  return (
                    <MenuItem
                      key={item.path}
                      icon={<IconComponent />}
                      {...linkProps}
                      onClick={(e: React.MouseEvent) => {
                        if (!item.openInNewTab && !isModifiedClick(e)) onNavigate?.();
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  );
                })}
              </MenuList>
            </MenuPopover>
          </Menu>
        )}
      </div>

      <div className="left-rail-bottom">
        {bottomNavItems.map(renderButton)}
        <button
          className="left-rail-toggle"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-label={isExpanded ? 'Collapse left rail' : 'Expand left rail'}
        >
          {isExpanded ? <ChevronLeft24Regular /> : <ChevronRight24Regular />}
        </button>
      </div>
    </nav>
  );
}
